import type { ReactNode } from 'react'

import type { AppLocale } from '@/lib/i18n/locales'
import { cn } from '@/lib/utils'

/** Five-point star polygon centred on (cx, cy). */
function star(cx: number, cy: number, r: number): string {
  return Array.from({ length: 10 }, (_, i) => {
    const radius = i % 2 === 0 ? r : r * 0.4
    const angle = -Math.PI / 2 + (i * Math.PI) / 5
    return `${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`
  }).join(' ')
}

const STRIPE_H = 20 / 13

// Simplified for ~20px circles: fine detail (Spain's arms, US star field) is reduced
// to shapes that still read correctly at icon size.
const FLAGS: Record<AppLocale, ReactNode> = {
  en: (
    <>
      <rect width="20" height="20" fill="#fff" />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} y={i * 2 * STRIPE_H} width="20" height={STRIPE_H} fill="#B22234" />
      ))}
      <rect width="10" height={7 * STRIPE_H} fill="#3C3B6E" />
      {[2, 5, 8].flatMap((x) => [2, 5, 8].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.7" fill="#fff" />))}
    </>
  ),
  es: (
    <>
      <rect width="20" height="20" fill="#AA151B" />
      <rect y="5" width="20" height="10" fill="#F1BF00" />
    </>
  ),
  ja: (
    <>
      <rect width="20" height="20" fill="#fff" />
      <circle cx="10" cy="10" r="5.5" fill="#BC002D" />
    </>
  ),
  ko: (
    <>
      <rect width="20" height="20" fill="#fff" />
      <circle cx="10" cy="10" r="5" fill="#0047A0" />
      <path d="M5,10 A5,5 0 0 1 15,10 A2.5,2.5 0 0 1 10,10 A2.5,2.5 0 0 0 5,10 Z" fill="#CD2E3A" />
      {[45, 135, 225, 315].map((deg) => (
        <g key={deg} transform={`rotate(${deg} 10 10)`} fill="#000">
          <rect x="8" y="1.2" width="4" height="0.7" />
          <rect x="8" y="2.3" width="4" height="0.7" />
          <rect x="8" y="3.4" width="4" height="0.7" />
        </g>
      ))}
    </>
  ),
  de: (
    <>
      <rect width="20" height="7" fill="#000" />
      <rect y="6.67" width="20" height="6.67" fill="#DD0000" />
      <rect y="13.33" width="20" height="6.67" fill="#FFCE00" />
    </>
  ),
  zh: (
    <>
      <rect width="20" height="20" fill="#EE1C25" />
      <polygon points={star(6, 7, 3.6)} fill="#FFFF00" />
      {[
        [10.5, 3],
        [12.5, 5.5],
        [12.5, 8.5],
        [10.5, 11],
      ].map(([x, y]) => (
        <polygon key={`${x}-${y}`} points={star(x, y, 1.1)} fill="#FFFF00" />
      ))}
    </>
  ),
  th: (
    <>
      <rect width="20" height="20" fill="#A51931" />
      <rect y="3.33" width="20" height="13.33" fill="#F4F5F8" />
      <rect y="6.67" width="20" height="6.67" fill="#2D2A4A" />
    </>
  ),
}

export function LocaleFlag({ locale, className }: { locale: AppLocale; className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      className={cn('size-5 shrink-0 overflow-hidden rounded-full ring-1 ring-white/15', className)}
    >
      {FLAGS[locale]}
    </svg>
  )
}
